import { useShoppingCart } from "../context/ShoppingCartContext";
import { formatCurrency } from "../utilities/formatCurrency";
import { Dash, Plus, Trash3 } from "react-bootstrap-icons";
import { useProduct, useProductOptionItems } from "../lib/query/queries/useProductQueries";
import { useCachedImageSrc } from "../hooks/useCachedImageSrc";

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function CartItemSkeleton() {
  const pulse = { backgroundColor: "rgba(118,118,128,0.12)", borderRadius: 6 };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 0" }}>
      <div style={{ ...pulse, width: 60, height: 60, borderRadius: 10, flexShrink: 0 }} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ ...pulse, height: 14, width: "65%" }} />
        <div style={{ ...pulse, height: 11, width: "40%", borderRadius: 999 }} />
        <div style={{ ...pulse, height: 12, width: "35%" }} />
        <div style={{ ...pulse, height: 24, width: 80, borderRadius: 8 }} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8, flexShrink: 0 }}>
        <div style={{ ...pulse, height: 16, width: 44 }} />
        <div style={{ ...pulse, height: 20, width: 20, borderRadius: "50%" }} />
      </div>
    </div>
  );
}

// ─── CartItem ─────────────────────────────────────────────────────────────────

export function CartItem({ id, quantity, selectedOptions }) {
  const { removeFromCart, increaseCartQuantity, decreaseCartQuantity } = useShoppingCart();
  // Cached by product id — the same product shown in cart, checkout, and
  // order history now shares one fetch instead of each issuing its own.
  const { data: item = { id: "", name: "", imageSrc: "", price: 0, storeId: "" }, isLoading } =
    useProduct(id);
  const { data: resolvedOptions } = useProductOptionItems(id, selectedOptions);
  const cachedImageSrc = useCachedImageSrc(item.imageSrc);

  const optionsAmount = resolvedOptions.reduce((sum, o) => sum + (o.amount || 0), 0);

  if (isLoading) return <CartItemSkeleton />;

  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 0" }}>

      {/* Image */}
      <div style={{
        width: 60, height: 60, borderRadius: 10, flexShrink: 0,
        backgroundColor: "rgba(118,118,128,0.08)",
        overflow: "hidden",
      }}>
        <img
          src={cachedImageSrc}
          alt={item.name}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </div>

      {/* All content in one column — flex:1 + minWidth:0 prevents overflow */}
      <div style={{ flex: 1, minWidth: 0 }}>

        {/* Row 1: name + line total on the same baseline */}
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 5 }}>
          <span style={{
            flex: 1, minWidth: 0,
            fontSize: "0.9375rem", fontWeight: 600, color: "#1c1c1e",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {item.name}
          </span>
          <span style={{
            flexShrink: 0,
            fontSize: "0.9375rem", fontWeight: 700, color: "#1c1c1e",
            letterSpacing: "-0.018em",
          }}>
            {formatCurrency((item.price + optionsAmount) * quantity)}
          </span>
        </div>

        {/* Row 2: option pills */}
        {resolvedOptions.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 5 }}>
            {resolvedOptions.map((opt, i) => (
              <span key={i} style={{
                display: "inline-flex", alignItems: "center", gap: 3,
                fontSize: "0.6875rem", fontWeight: 500,
                color: "#007AFF", backgroundColor: "rgba(0,122,255,0.08)",
                borderRadius: 999, padding: "3px 8px",
                whiteSpace: "nowrap", letterSpacing: "-0.005em",
              }}>
                {opt.value}
                {opt.amount > 0 && (
                  <span style={{ opacity: 0.6, fontWeight: 400 }}>
                    +{formatCurrency(opt.amount)}
                  </span>
                )}
              </span>
            ))}
          </div>
        )}

        {/* Row 3: unit price */}
        {quantity > 1 && (
          <div style={{ fontSize: "0.78125rem", color: "rgba(60,60,67,0.5)", marginBottom: 9 }}>
            {formatCurrency(item.price + optionsAmount)} each
          </div>
        )}

        {/* Row 4: qty stepper + remove */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 10,
            backgroundColor: "rgba(118,118,128,0.1)",
            borderRadius: 8, padding: "5px 10px",
          }}>
            <button
              onClick={() => decreaseCartQuantity(item.id)}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center", color: "#007aff", lineHeight: 1 }}
            >
              <Dash size={15} />
            </button>
            <span style={{ fontSize: "0.875rem", fontWeight: 600, color: "#1c1c1e", minWidth: 14, textAlign: "center", lineHeight: 1 }}>
              {quantity}
            </span>
            <button
              onClick={() => increaseCartQuantity(item.id, item.storeId, null)}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center", color: "#007aff", lineHeight: 1 }}
            >
              <Plus size={15} />
            </button>
          </div>
          <button
            onClick={() => removeFromCart(item.id)}
            style={{
              background: "none", border: "none", cursor: "pointer", padding: 4,
              color: "rgba(60,60,67,0.35)", lineHeight: 1,
              display: "flex", alignItems: "center",
            }}
          >
            <Trash3 size={15} />
          </button>
        </div>

      </div>

    </div>
  );
}
